import React, { useState } from 'react';
import { Box, Divider, Input, List, ListItemButton, ListItemText, Paper, Typography } from '@mui/material';

import { User } from '@paragon/contract-library';
import { createScrollbarStyling } from '@paragon/themes';

import { getUsersSortedByFirstName, loggedInUser } from '../../selectors/users';
import { selectUser } from '../../store/userDataSlice';
import { CsvExport } from '../CsvExport';
import { CsvImport } from '../CsvImport';
import { useAppDispatch, useAppSelector } from '../hooks';

const getEmailDomain = (email: string) => email.slice(email.indexOf('@') + 1);

export function UserList() {
  const dispatch = useAppDispatch();

  const users = useAppSelector(getUsersSortedByFirstName);
  const loggedInUserValue = useAppSelector(loggedInUser);

  const [searchValue, setSearchValue] = useState('');

  const onSearchChange: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (event) => {
    setSearchValue(event.target.value.trimStart());
  };

  const onUserClick = (userEmail: string) => {
    dispatch(selectUser(userEmail));
  };

  function getFilteredListItems() {
    if (searchValue.length !== 0) {
      return users.filter(
        (user) =>
          user.email.toLowerCase().startsWith(searchValue.toLowerCase()) ||
          getEmailDomain(user.email.toLowerCase()).startsWith(searchValue.toLowerCase()) ||
          user.firstName.toLowerCase().startsWith(searchValue.toLowerCase()) ||
          user.lastName.toLowerCase().startsWith(searchValue.toLowerCase()),
      );
    }
    return users;
  }

  const renderListItem = (user: User, index: number) => (
    <ListItemButton
      onClick={() => {
        onUserClick(user.email);
      }}
      key={index}
    >
      <ListItemText primary={user.email} secondary={`${user.firstName} ${user.lastName}`} />
    </ListItemButton>
  );

  function renderUserList() {
    return (
      <List
        sx={[
          { bgcolor: 'backContrast.light', maxHeight: 500, mb: 3, overflowX: 'hidden', overflowY: 'auto' },
          createScrollbarStyling,
        ]}
      >
        {getFilteredListItems().map(renderListItem)}
      </List>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '25%', p: 1 }}>
      <Typography variant="h6">User List</Typography>
      <Divider sx={{ mb: 4 }} />
      <Paper sx={{ p: 2, pt: 0, bgcolor: 'backContrast.main' }} elevation={1}>
        <Box sx={!loggedInUserValue!.isRoot ? { display: 'none' } : { mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-evenly', py: 2 }}>
            <CsvExport />
            <CsvImport />
          </Box>
        </Box>
        <Input
          placeholder="Search..."
          onChange={onSearchChange}
          sx={{ mb: 2 }}
          value={searchValue}
          inputProps={{
            'aria-label': 'Description',
          }}
        />
        {renderUserList()}
      </Paper>
    </Box>
  );
}
